import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/Common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, Award, ShieldCheck, BookOpen, ClipboardList, Loader2, Filter, X, Wrench, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { type: "all", title: "Full Board Inspection Report", desc: "Licenses, compliance, SOP catalog, work orders and COA library in one document.", icon: ClipboardList, accent: "bg-pink-50 text-pink-700" },
  { type: "licenses", title: "Licensing Report", desc: "Facility, pharmacist, technician and DEA licenses with expirations.", icon: Award, accent: "bg-sky-50 text-sky-600" },
  { type: "compliance", title: "Compliance & Validation Report", desc: "Clean room validation, media fills, and competency tasks with due dates.", icon: ShieldCheck, accent: "bg-amber-50 text-amber-600" },
  { type: "sops", title: "SOP Catalog Report", desc: "Every SOP with version and staff sign-off counts.", icon: BookOpen, accent: "bg-emerald-50 text-emerald-600" },
  { type: "work_orders", title: "Facility Work Orders Report", desc: "AC tech, plumber, electrical and equipment vendor calls with status and cost.", icon: Wrench, accent: "bg-orange-50 text-orange-600" },
  { type: "coa", title: "COA Library Report", desc: "Certificates of analysis by supplier, lot and receive date.", icon: FileCheck2, accent: "bg-indigo-50 text-indigo-600" },
];

export default function Reports() {
  const [busy, setBusy] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [employee, setEmployee] = useState("");

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const buildQuery = (t) => {
    const p = new URLSearchParams({ type: t });
    if (fromDate) p.set("from", fromDate);
    if (toDate) p.set("to", toDate);
    if (employee) p.set("employee", employee);
    return p.toString();
  };

  const clearFilters = () => { setFromDate(""); setToDate(""); setEmployee(""); };
  const hasFilters = fromDate || toDate || employee;

  const download = async (r) => {
    setBusy(r.type);
    try {
      const res = await api.get(`/reports/inspection?${buildQuery(r.type)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const suffix = hasFilters ? "-filtered" : "";
      a.href = url; a.download = `nucleo-${r.type}${suffix}-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error("Could not generate report"); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader testid="reports-header" title="Inspection Reports" subtitle="Export clean, board-ready PDFs for state inspections." />

      <Card className="p-5 border-slate-200 mb-5" data-testid="report-filters-card">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-pink-700" />
          <h3 className="font-heading font-semibold text-slate-800">Filters</h3>
          {hasFilters && (
            <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={clearFilters} className="ml-auto h-8 text-slate-500 gap-1">
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-600">From date</Label>
            <Input data-testid="filter-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">To date</Label>
            <Input data-testid="filter-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Employee</Label>
            <Select value={employee || "__all__"} onValueChange={(v) => setEmployee(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="filter-employee" className="mt-1">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All employees</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Licenses filter by expiration date · Compliance tasks by due date · SOPs by created date.
          Employee filter applies to license holder, task assignee, and SOP sign-offs.
        </p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.type} data-testid={`report-card-${r.type}`} className="p-5 border-slate-200 flex items-start gap-4 hover:shadow-md transition-shadow">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${r.accent}`}><r.icon className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-slate-800">{r.title}</h3>
              <p className="text-sm text-slate-500 mt-0.5">{r.desc}</p>
              <Button data-testid={`report-download-${r.type}`} onClick={() => download(r)} disabled={busy === r.type} className="mt-3 bg-pink-700 hover:bg-pink-800 gap-1.5 h-9">
                {busy === r.type ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Export PDF
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
